import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PlayerSectionData } from '@/types/unifiedGameForm';
import { GameWithDetails } from '@/types/gameSystem';
import { useAuthenticatedUser } from '@/hooks/useAuthenticatedUser';
import { supabase } from '@/integrations/supabase/client';
import { ATTENDANCE_OPTIONS } from '@/types/playerStatus';
import { User, Loader2, Save, UserPlus, Clock, UserMinus } from 'lucide-react';
import { toast } from 'sonner';
import { differenceInMinutes, parseISO, format } from 'date-fns';
import { gameParticipantsService } from '@/services/gameParticipantsService';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';

interface PlayerSectionProps {
  gameData: GameWithDetails;
  canEdit: boolean;
  canApprove: boolean;
  onSave: (data: PlayerSectionData) => void;
  saving: boolean;
}

export const PlayerSection: React.FC<PlayerSectionProps> = ({
  gameData,
  canEdit,
  canApprove,
  onSave,
  saving
}) => {
  const { user } = useAuthenticatedUser();
  const [isParticipant, setIsParticipant] = useState<boolean>(false);
  const [checkingParticipation, setCheckingParticipation] = useState(true);
  
  const [formData, setFormData] = useState<PlayerSectionData>({
    attendance: {
      status: '' as any // Start with blank/null until user selects
    }
  });
  const [currentAttendanceStatus, setCurrentAttendanceStatus] = useState<string | null>(null);
  const [isEditingLocked, setIsEditingLocked] = useState(false);
  const [minutesUntilGame, setMinutesUntilGame] = useState<number | null>(null);
  const [participationStatus, setParticipationStatus] = useState<string | null>(null);
  const [isLeavingGame, setIsLeavingGame] = useState(false);

  // Check time restrictions and lock editing if within 5 minutes of game time
  useEffect(() => {
    const checkTimeRestrictions = () => {
      if (!gameData.game_date || !gameData.game_time) return;

      try {
        const gameDateTime = new Date(`${gameData.game_date}T${gameData.game_time}`);
        const now = new Date();
        const minutesUntil = differenceInMinutes(gameDateTime, now);
        
        setMinutesUntilGame(minutesUntil);
        setIsEditingLocked(minutesUntil <= 5 && minutesUntil > 0);
      } catch (error) {
        console.error('Error calculating game time:', error);
      }
    };

    checkTimeRestrictions();
    const interval = setInterval(checkTimeRestrictions, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [gameData.game_date, gameData.game_time]);

  // Check if user is a participant, coach, or team owner and load current attendance status
  useEffect(() => {
    const checkParticipationAndLoadStatus = async () => {
      if (!user || !gameData.id) return;

      try {
        // First check if user is a coach or team owner for this game
        const { data: coachData } = await supabase
          .from('team_coaches')
          .select('team_id')
          .eq('coach_id', user.id)
          .in('team_id', [gameData.home_team_id, gameData.away_team_id].filter(Boolean));

        const { data: ownerData } = await supabase
          .from('teams')
          .select('team_id')
          .eq('created_by', user.id)
          .in('team_id', [gameData.home_team_id, gameData.away_team_id].filter(Boolean));

        const isCoach = coachData && coachData.length > 0;
        const isTeamOwner = ownerData && ownerData.length > 0;

        // If user is a coach or team owner, set special status
        if (isCoach || isTeamOwner) {
          setIsParticipant(true);
          setParticipationStatus(isCoach ? 'coach' : 'team_owner');
          setCheckingParticipation(false);
          return;
        }

        // Check if user is a participant
        const { data: participantData, error: participantError } = await supabase
          .from('game_participants')
          .select('*')
          .eq('game_id', gameData.id)
          .eq('player_id', user.id)
          .maybeSingle();

        if (participantError && participantError.code !== 'PGRST116') {
          console.error('Error checking participation:', participantError);
          return;
        }

        const isConfirmedParticipant = !!participantData;
        setIsParticipant(isConfirmedParticipant);
        setParticipationStatus(participantData?.participation_status || null);

        // If not a confirmed participant, check for pending join request
        if (!isConfirmedParticipant) {
          const { data: joinRequestData, error: joinRequestError } = await supabase
            .from('join_requests')
            .select('*')
            .eq('game_id', gameData.id)
            .eq('player_id', user.id)
            .eq('request_status', 'pending')
            .maybeSingle();

          if (joinRequestError && joinRequestError.code !== 'PGRST116') {
            console.error('Error checking join request:', joinRequestError);
          } else if (joinRequestData) {
            setParticipationStatus('requested');
            setIsParticipant(true); // Show player section for pending requests too
          }
        }

        // Load attendance status only for confirmed participants
        if (isConfirmedParticipant) {
          const { data: attendanceData, error: attendanceError } = await supabase
            .from('player_attendance_status')
            .select('*')
            .eq('game_id', gameData.id)
            .eq('player_id', user.id)
            .maybeSingle();

          if (attendanceError && attendanceError.code !== 'PGRST116') {
            console.error('Error loading attendance status:', attendanceError);
          } else if (attendanceData) {
            setCurrentAttendanceStatus(attendanceData.attendance_status);
            // Set the form data to reflect current status
            setFormData({
              attendance: {
                status: attendanceData.attendance_status as 'attending' | 'not_attending'
              }
            });
          }
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setCheckingParticipation(false);
      }
    };

    checkParticipationAndLoadStatus();
  }, [user, gameData.id]);

  const handleInputChange = (value: 'attending' | 'not_attending') => {
    setFormData(prev => ({
      ...prev,
      attendance: {
        status: value
      }
    }));
  };

  const handleSave = async () => {
    if (!user || !gameData.id) return;
    if (isEditingLocked) {
      toast.error("Changes are locked - game starts in less than 5 minutes");
      return;
    }

    // Check if user has a pending join request
    if (participationStatus === 'requested') {
      toast.error("You cannot set attendance while your join request is pending approval");
      return;
    }

    try {
      console.log('=== ATTENDANCE SAVE DEBUG START ===');
      console.log('Current user info:', { 
        userId: user.id, 
        userType: 'email' in user ? 'supabase' : 'internal',
        userEmail: 'email' in user ? user.email : undefined,
        userName: 'username' in user ? user.username : undefined
      });
      console.log('Game info:', { 
        gameId: gameData.id,
        gameName: gameData.game_name,
        attendanceStatus: formData.attendance.status
      });
      
      console.log('Step 1: Fetching participant data...');
      // Fetch participant data to get both team_side and player_type
      const { data: participantData, error: participantError } = await supabase
        .from('game_participants')
        .select('team_side, player_type')
        .eq('game_id', gameData.id)
        .eq('player_id', user.id)
        .maybeSingle();

      if (participantError) {
        console.error('❌ Error fetching participant data:', {
          error: participantError,
          code: participantError.code,
          message: participantError.message,
          details: participantError.details
        });
        console.log('=== ATTENDANCE SAVE DEBUG END (PARTICIPANT ERROR) ===');
        toast.error("Failed to load participant information");
        return;
      }

      if (!participantData) {
        console.error('❌ No participant data found for user:', user.id, 'in game:', gameData.id);
        console.log('=== ATTENDANCE SAVE DEBUG END (NO PARTICIPANT) ===');
        toast.error("You are not registered as a participant for this game");
        return;
      }

      console.log('✅ Participant data found:', participantData);
      const teamSide = participantData.team_side as 'home' | 'away';
      const playerType = participantData.player_type as 'supabase' | 'internal';

      console.log('Step 2: Checking for existing attendance record...');
      // Check if attendance status already exists
      const { data: existingAttendance, error: existingError } = await supabase
        .from('player_attendance_status')
        .select('id')
        .eq('game_id', gameData.id)
        .eq('player_id', user.id)
        .maybeSingle();

      if (existingError) {
        console.error('❌ Error checking existing attendance:', {
          error: existingError,
          code: existingError.code,
          message: existingError.message
        });
        console.log('=== ATTENDANCE SAVE DEBUG END (EXISTING CHECK ERROR) ===');
        toast.error("Failed to check existing attendance status");
        return;
      }

      console.log('Existing attendance check result:', existingAttendance);

      const attendanceData = {
        game_id: gameData.id,
        player_id: user.id,
        player_type: playerType,
        team_side: teamSide,
        attendance_status: formData.attendance.status,
        set_by: user.id,
        set_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      console.log('Prepared attendance data:', attendanceData);

      let result;
      if (existingAttendance) {
        // Update existing record
        console.log('Step 3A: Updating existing attendance record:', existingAttendance.id);
        result = await supabase
          .from('player_attendance_status')
          .update({
            attendance_status: formData.attendance.status,
            set_by: user.id,
            set_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', existingAttendance.id);
          
        console.log('Update operation result:', result);
      } else {
        // Insert new record
        console.log('Step 3B: Inserting new attendance record');
        result = await supabase
          .from('player_attendance_status')
          .insert(attendanceData);
          
        console.log('Insert operation result:', result);
      }

      if (result.error) {
        console.error('❌ Database operation failed:', {
          error: result.error,
          code: result.error.code,
          message: result.error.message,
          details: result.error.details,
          hint: result.error.hint
        });
        console.log('=== ATTENDANCE SAVE DEBUG END (DB OPERATION ERROR) ===');
        toast.error(`Failed to save attendance status: ${result.error.message}`);
        return;
      }

      console.log('✅ Database operation successful');
      console.log('✅ Attendance status saved successfully');
      
      // If player selects "attending", auto-approve them
      if (formData.attendance.status === 'attending') {
        try {
          console.log('🎯 Triggering auto-approval for attending player...');
          const { data: approvalData, error: approvalError } = await supabase.functions.invoke('auto-approve-players', {
            body: {
              gameId: gameData.id,
              playerId: user.id,
              playerType: playerType,
              teamSide: teamSide
            }
          });

          if (approvalError) {
            console.warn('⚠️ Auto-approval failed:', approvalError);
            // Don't block the attendance save, just log the warning
          } else {
            console.log('✅ Player auto-approval successful:', approvalData);
          }
        } catch (approvalError) {
          console.warn('⚠️ Error during auto-approval:', approvalError);
          // Don't block the attendance save, just log the warning
        }
      }
      
      // If player selects "not_attending", revoke any existing approval
      if (formData.attendance.status === 'not_attending') {
        try {
          const { error: revokeError } = await supabase.functions.invoke('revoke-player-approval', {
            body: {
              gameId: gameData.id,
              playerId: user.id,
              playerType: playerType
            }
          });

          if (revokeError) {
            console.warn('Failed to revoke approval:', revokeError);
          } else {
            console.log('Player approval revoked successfully');
          }
        } catch (revokeError) {
          console.warn('Error revoking approval:', revokeError);
        }
      }

      console.log('=== ATTENDANCE SAVE DEBUG END (SUCCESS) ===');
      
      setCurrentAttendanceStatus(formData.attendance.status);
      toast.success("Attendance status updated successfully");

      // PlayerSection handles its own saving directly to player_attendance_status table
      // No need to call onSave as it would trigger unnecessary unified form submission
    } catch (error) {
      console.error('=== ATTENDANCE SAVE CATCH ERROR ===');
      console.error('Unexpected error:', {
        error,
        name: error?.name,
        message: error?.message,
        stack: error?.stack
      });
      console.log('=== ATTENDANCE SAVE DEBUG END (CATCH ERROR) ===');
      toast.error("Failed to save attendance status. Please try again.");
    }
  };

  const handleLeaveGame = async () => {
    if (!user || !gameData.id) return;
    
    setIsLeavingGame(true);
    try {
      let success = false;
      
      if (participationStatus === 'requested') {
        // Cancel pending join request
        success = await gameParticipantsService.cancelJoinRequest(gameData.id, user.id);
        if (success) {
          toast.success("Join request canceled successfully");
        }
      } else {
        // Remove from confirmed game
        const playerType = 'email' in user ? 'supabase' : 'internal'; // Determine player type
        success = await gameParticipantsService.removePlayerSelfFromGame(
          gameData.id, 
          user.id, 
          playerType
        );
        if (success) {
          toast.success("Successfully left the game. Coaches have been notified.");
        }
      }
      
      if (success) {
        // Update local state
        setIsParticipant(false);
        setParticipationStatus(null);
        setCurrentAttendanceStatus(null);
        // Reset form
        setFormData({
          attendance: {
            status: '' as any
          }
        });
      } else {
        toast.error("Failed to leave game. Please try again.");
      }
    } catch (error) {
      console.error('Error leaving game:', error);
      toast.error("Failed to leave game. Please try again.");
    } finally {
      setIsLeavingGame(false);
    }
  };

  // Helper function for badge styling
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'attending':
        return 'default';
      case 'not_attending':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  // Determine if editing should be allowed
  const canEditAttendance = canEdit && !isEditingLocked;
  
  // Determine leave button text based on participation status
  const getLeaveButtonText = () => {
    if (participationStatus === 'requested') {
      return 'Cancel Request';
    }
    return 'Leave Game';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          Player Status
          {!canEdit && <Badge variant="outline">View Only</Badge>}
        </CardTitle>
        <CardDescription>
          Confirm your attendance for this game. Your coach will manage readiness status.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {checkingParticipation ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span className="text-sm text-muted-foreground">Checking participation status...</span>
          </div>
        ) : !isParticipant ? (
          <div className="text-center py-8 space-y-4">
            <div className="flex items-center justify-center">
              <UserPlus className="h-12 w-12 text-muted-foreground" />
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-2">Not a Game Participant</h4>
              <p className="text-muted-foreground">
                You need to be registered as a participant to set your attendance status.
              </p>
            </div>
          </div>
        ) : participationStatus === 'coach' || participationStatus === 'team_owner' ? (
          <div className="text-center py-8 space-y-4">
            <div className="flex items-center justify-center">
              <User className="h-12 w-12 text-muted-foreground" />
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-2">View Only - {participationStatus === 'coach' ? 'Coach' : 'Team Owner'}</h4>
              <p className="text-muted-foreground">
                As a {participationStatus === 'coach' ? 'coach' : 'team owner'}, you manage player readiness in the Coach Management section.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Join Request Pending Status */}
            {participationStatus === 'requested' && (
              <div className="p-4 border border-blue-200 rounded-lg bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800">
                <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    Your join request is pending approval from the coach
                  </span>
                </div>
                <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                  You cannot set attendance status until your request is approved
                </p>
              </div>
            )}

            {/* Time Lock Warning */}
            {isEditingLocked && participationStatus !== 'requested' && (
              <div className="p-4 border border-amber-200 rounded-lg bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800">
                <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    Changes locked - Game starts in {minutesUntilGame} minutes
                  </span>
                </div>
              </div>
            )}

            {/* Attendance Status */}
            {participationStatus !== 'requested' && (
              <div className="space-y-4">
                <h4 className="text-sm font-semibold">Attendance Status</h4>
                <div className="space-y-4">
                   <div>
                     <p className="text-xs md:text-sm text-muted-foreground mb-3">
                       Will you be attending this game? {isEditingLocked ? '(Changes locked)' : '(Changes allowed until 5 minutes before game time)'}
                     </p>
                     {canEditAttendance ? (
                       <Select
                         value={formData.attendance.status}
                         onValueChange={(value) => handleInputChange(value as 'attending' | 'not_attending')}
                       >
                         <SelectTrigger className="h-11 md:h-10 text-sm">
                           <SelectValue placeholder="Select your attendance" />
                         </SelectTrigger>
                         <SelectContent className="bg-background border z-50">
                           {ATTENDANCE_OPTIONS.map((option) => (
                             <SelectItem 
                               key={option.value} 
                               value={option.value}
                               className="py-3 md:py-2"
                             >
                               {option.label}
                             </SelectItem>
                           ))}
                         </SelectContent>
                       </Select>
                     ) : (
                       <div className="p-3 border rounded-lg bg-muted/50">
                         <Badge variant={getStatusBadgeVariant(formData.attendance.status)}>
                           {formData.attendance.status === 'attending' ? 'Attending' : 'Not Attending'}
                         </Badge>
                       </div>
                     )}
                   </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center gap-3 md:gap-0">
              {/* Leave Game Button */}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={isLeavingGame || (isEditingLocked && participationStatus !== 'requested')}
                    className="text-destructive hover:text-destructive border-destructive hover:bg-destructive/10 h-11 md:h-9 text-sm"
                  >
                    {isLeavingGame ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Leaving...
                      </>
                    ) : (
                      <>
                        <UserMinus className="h-4 w-4 mr-2" />
                        {getLeaveButtonText()}
                      </>
                    )}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {participationStatus === 'requested' ? 'Cancel Join Request?' : 'Leave Game?'}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {participationStatus === 'requested' 
                        ? 'This will cancel your join request for this game. You can request to join again later.'
                        : 'This will remove you from the game. Your coaches and team owners will be notified. You may need to request to join again if you change your mind.'
                      }
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={handleLeaveGame}
                      className="bg-destructive hover:bg-destructive/90"
                    >
                      {participationStatus === 'requested' ? 'Cancel Request' : 'Leave Game'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              {/* Save Button - only show for confirmed participants */}
              {canEditAttendance && participationStatus !== 'requested' && (
                <Button 
                  onClick={handleSave} 
                  disabled={saving || isEditingLocked || !formData.attendance.status}
                  className="h-11 md:h-9 text-sm"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Confirm Status
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};